import { QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, httpLink } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@/types/appRouter';
import superjson from 'superjson';
import { buildHeadersWithCsrfToken } from '@/api/csrf';

export const queryClient = new QueryClient();
const hostname = window?.location?.hostname ?? '';
const isLocalHostname = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';
export const API_DOMAIN = isLocalHostname ? 'http://localhost:8081' : 'https://backend.project314.dev';

type TrpcFetchInit =
    | RequestInit
    | {
          body?: FormData | string | null | Uint8Array<ArrayBuffer> | Blob | File;
          headers?: [string, string][] | Record<string, string>;
          method?: string;
          signal?: AbortSignal | undefined;
      };

const fetchWithCredentialsAndCsrf = async (url: RequestInfo | URL | string, options?: TrpcFetchInit): Promise<Response> => {
    const makeRequest = async (params?: { forceRefreshCsrf?: boolean }) => {
        const headers = await buildHeadersWithCsrfToken({
            apiDomain: API_DOMAIN,
            headers: options?.headers,
            forceRefresh: params?.forceRefreshCsrf
        });

        return fetch(url, {
            ...options,
            credentials: 'include',
            headers
        });
    };

    let response = await makeRequest();

    if (!response.ok && response.status === 403) {
        let errorMessages: string[] | null = null;

        try {
            const json = (await response.clone().json()) as { errorMessages?: unknown };

            if (Array.isArray(json.errorMessages)) {
                errorMessages = json.errorMessages.filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
            }
        } catch {
            // ignore
        }

        if ((errorMessages?.[0] ?? null) === 'CSRF validation failed') {
            response = await makeRequest({ forceRefreshCsrf: true });
        }
    }

    return response;
};

export const trpcClient = createTRPCClient<AppRouter>({
    links: [
        httpBatchLink({
            url: `${API_DOMAIN}/trpc`,
            transformer: superjson,
            fetch: fetchWithCredentialsAndCsrf
        })
    ]
});

// 4) Unbatched TRPC client for single immediate calls
export const trpcUnbatchedClient = createTRPCClient<AppRouter>({
    links: [
        httpLink({
            url: `${API_DOMAIN}/trpc`,
            transformer: superjson,
            fetch: fetchWithCredentialsAndCsrf
        })
    ]
});

// POST-override client for explicitly opted-in large query payloads.
export const trpcPostUnbatchedClient = createTRPCClient<AppRouter>({
    links: [
        httpLink({
            url: `${API_DOMAIN}/trpc`,
            transformer: superjson,
            methodOverride: 'POST',
            fetch: fetchWithCredentialsAndCsrf
        })
    ]
});

// 5) Proxies for useQuery/useMutation hooks
export const trpc = createTRPCOptionsProxy<AppRouter>({
    client: trpcClient,
    queryClient
});

export const trpcUnbatched = createTRPCOptionsProxy<AppRouter>({
    client: trpcUnbatchedClient,
    queryClient
});

export const trpcPostUnbatched = createTRPCOptionsProxy<AppRouter>({
    client: trpcPostUnbatchedClient,
    queryClient
});
